import { Group, Grid, TextInput, Button, Accordion, JsonInput, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { addLocation, updateLocation, deleteLocation, ILocation, LocationRequest } from '../../../libraries/location';
import Header from '../util/header';
import { CountrySelect } from './country-select';
import { MapCoordinates } from './maps-coordinates';
import { Language } from '../../languages/languageHandler';
import { ICurrency, ICurrencyValuesForm } from '../../../libraries/currencies';
import ChooseEntityModal from '../../helpdesk-app/components/choose-entity-modal';
import Input from '../../components/inputs';
import CurrencyTable from '../../pages/equipment-config/currencies-table';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { StatusType } from '../../../libraries/zps';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import './styles.scss';

enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new',
  DEFAULT = 'default'
}

interface LocationFormProps {
  lang: Language;
  location: ILocation | null;
  currencyList: ICurrency[];
  selectedEntity?: number;
  loading: boolean;
  mode?: MODE;
  workflow?: 'helpdesk' | 'locals';
  reloadLocations?: () => void;
  clear?: () => void;
}

const LocationsForm: React.FC<LocationFormProps> = ({
  lang,
  location,
  currencyList,
  loading,
  mode = MODE.DEFAULT,
  workflow,
  reloadLocations,
  clear,
  selectedEntity
}) => {
  const navigate = useNavigate();
  const controller = useMemo(() => new AbortController(), []);
  const maxCurrencies = useSelector<AppState, number>(state => state.login.entity?.maxCurrencies || 1);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [declarationAmount] = useState<number | undefined>(undefined);
  const [chooseEntityModal, setChooseEntityModal] = useState<boolean>(false);
  const [selectedCurrencies, setSelectedCurrencies] = useState<ICurrencyValuesForm[]>([]);
  const filteredCid = currencyList.filter(c => !selectedCurrencies.some(sc => sc.cid === c.cid));
  const [isNewLocation, setNewLocation] = useState(true);
  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  useEffect(() => {
    form.setFieldValue('currencies', selectedCurrencies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrencies]);

  useEffect(() => {
    if (selectedEntity) {
      form.reset();
      form.setFieldValue('entid', selectedEntity);
      setSelectedCurrencies([]);
      setCoordinatesInput('0, 0');
      setIsMapOpen(false);
      setNewLocation(true);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const form = useForm<LocationRequest>({
    initialValues: {
      sid: location?.sid,
      entid: location?.entid ?? selectedEntity,
      name: location?.name ?? '',
      description: location?.description ?? '',
      address: location?.address ?? '',
      postalCode: location?.postalCode ?? '',
      country: location?.country ?? '',
      lat: location?.lat ?? 0,
      lng: location?.lng ?? 0,
      external: location?.external ?? '',
      cbendpoint: location?.cbendpoint ?? ''
    }
  });

  useEffect(() => {
    if (!location) return;

    const mapped: ICurrencyValuesForm[] = (location.currencies ?? []).map(c => ({
      cid: c.cid,
      gid: c.gid ?? 0,
      entid: c.entid ?? location.entid ?? 0,
      eid: location.sid ?? 0,
      minAmount: c.minAmount ?? 0,
      maxAmount: c.maxAmount ?? 0,
      decAmount: c.decAmount ?? 0
    }));

    form.setValues({
      sid: location.sid,
      entid: location.entid,
      name: location.name ?? '',
      description: location.description ?? '',
      address: location.address ?? '',
      postalCode: location.postalCode ?? '',
      country: location.country ?? '',
      lat: location.lat ?? 0,
      lng: location.lng ?? 0,
      external: location.external ?? '',
      cbendpoint: location.cbendpoint ?? ''
    });

    setSelectedCurrencies(mapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleAddCurrency = (currency: ICurrencyValuesForm) => {
    setSelectedCurrencies(prev => {
      if (prev.some(c => c.cid === currency.cid)) return prev;

      const updated = [
        ...prev,
        {
          gid: currency.gid,
          cid: currency.cid,
          entid: currency.entid,
          sid: form.values.sid,
          minAmount: currency.minAmount || 0,
          maxAmount: currency.maxAmount || 0,
          decAmount: currency.decAmount || 0
        }
      ];

      form.setFieldValue('currencies', updated);
      return updated;
    });
  };
  const handleRemoveCurrency = (cid: number) => {
    setSelectedCurrencies(prev => {
      const updated = prev.filter(c => c.cid !== cid);
      form.setFieldValue('currencies', updated);
      return updated;
    });
  };

  const submitForm = async (values: LocationRequest) => {
    const request: LocationRequest = {
      ...values,
      currencies: values.currencies
    };

    const optionalKeys: (keyof LocationRequest)[] = [
      'sid',
      'description',
      'external',
      'cbendpoint',
      'address',
      'postalCode',
      'country',
      'lat',
      'lng'
    ];

    optionalKeys.forEach(key => {
      const value = request[key];
      if (value === '' || value === null || value === undefined) {
        delete request[key];
      }
    });

    const effectiveMode = mode === MODE.DEFAULT ? (values.sid ? MODE.EDIT : MODE.NEW) : mode;

    try {
      const response =
        effectiveMode === MODE.NEW ? await addLocation(request, controller) : await updateLocation(request, controller);

      const isSuccess = isZarphSuccessResponse(response);

      showStatusOperDialog({
        type: isSuccess ? StatusType.INFO : StatusType.ERROR,
        message: isSuccess
          ? effectiveMode === MODE.NEW
            ? lang.LOCATION_ADD_ALERT_SUCCESS
            : lang.LOCATION_UPDATE_ALERT_SUCCESS
          : effectiveMode === MODE.NEW
            ? lang.LOCATION_ADD_ALERT_ERROR
            : lang.LOCATION_UPDATE_ALERT_ERROR,
        statusOper: response.data.statusOper
      });

      if (isSuccess && effectiveMode === MODE.NEW) {
        const newLocation = response.data.shops?.[0];

        if (newLocation?.sid && newLocation?.entid) {
          if (workflow === 'helpdesk') {
            navigate(`/helpdesk/location/${MODE.EDIT}/${newLocation.entid}/${newLocation.sid}`);
          } else {
            navigate(`/locals`);
          }
        }
      }

      if (isSuccess && effectiveMode === MODE.EDIT) {
        reloadLocations?.();
      }
    } catch (error) {
      handleApiError(error, lang.LOCATION_UPDATE_ALERT_ERROR);
    }
  };
  const handleDeleteLocation = async (entid: number, sid: number) => {
    if (!sid) return;

    try {
      const response = await deleteLocation(entid, sid);
      const isSuccess = isZarphSuccessResponse(response);

      showStatusOperDialog({
        type: isSuccess ? StatusType.INFO : StatusType.ERROR,
        message: isSuccess ? lang.LOCATION_DELETE_ALERT_SUCCESS : lang.LOCATION_DELETE_ALERT_ERROR,
        statusOper: response.data.statusOper
      });

      if (isSuccess) {
        form.reset();
        setSelectedCurrencies([]);

        if (mode === MODE.DEFAULT && reloadLocations) {
          reloadLocations();
        } else {
          navigate('/helpdesk/locations');
        }
      }
    } catch (error) {
      handleApiError(error, lang.LOCATION_DELETE_ALERT_ERROR);
    }
  };

  const [coordinatesInput, setCoordinatesInput] = useState(`${form.values.lat}, ${form.values.lng}`);

  useEffect(() => {
    setCoordinatesInput(`${form.values.lat}, ${form.values.lng}`);
  }, [form.values.lat, form.values.lng]);

  const isReadMode = mode === MODE.READ;
  const isDefaultMode = mode === MODE.DEFAULT;

  const toggleChooseEntityModal = () => {
    setChooseEntityModal(!chooseEntityModal);
  };

  const chooseEntid = (entid: number) => {
    form.setFieldValue('entid', entid);
    setChooseEntityModal(false);
  };

  const clearEntid = () => {
    form.setFieldValue('entid', 0);
  };

  const handleEntidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      form.setFieldValue('entid', value === '' ? 0 : parseInt(value));
    }
  };

  const handleSidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      form.setFieldValue('sid', value === '' ? undefined : parseInt(value));
    }
  };

  const handleClearForm = () => {
    form.reset();
    setSelectedCurrencies([]);
    setCoordinatesInput('0, 0');
    setIsMapOpen(false);
    setNewLocation(true);
    clear?.();
  };

  return (
    <Group className="locals-edit">
      <ChooseEntityModal
        lang={lang}
        isOpen={chooseEntityModal}
        hideBtn={toggleChooseEntityModal}
        chooseEntid={chooseEntid}
      />

      {/* Entity Selector */}
      {mode === 'new' && (
        <Input
          label={lang.USER_ENTITY}
          value={form.values.entid !== undefined && form.values.entid !== 0 ? String(form.values.entid) : ''}
          disabled={!isNewLocation}
          isEditable
          search={toggleChooseEntityModal}
          onChange={handleEntidChange}
          onDelete={clearEntid}
        />
      )}

      {(mode === MODE.NEW && form.values.entid && form.values.entid !== 0) || mode !== MODE.NEW ? (
        <form onSubmit={form.onSubmit(submitForm)} className="locals-form">
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="ID"
                placeholder={lang.ENTITY_AUTO_ENTID}
                disabled={isReadMode}
                value={form.values.sid !== undefined ? String(form.values.sid) : ''}
                onChange={handleSidChange}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput
                label={lang.GLOBAL_NAME}
                required
                disabled={isReadMode}
                {...form.getInputProps('name')}
                value={form.values.name}
              />
            </Grid.Col>
          </Grid>
          <TextInput
            label={lang.EQUIPMENT_DESCRIPTION}
            disabled={isReadMode}
            {...form.getInputProps('description')}
            value={form.values.description}
          />
          <TextInput
            label={lang.GLOBAL_ADDRESS}
            disabled={isReadMode}
            {...form.getInputProps('address')}
            value={form.values.address}
          />
          <Grid>
            <Grid.Col span={6}>
              <CountrySelect
                form={form}
                label={lang.GLOBAL_COUNTRY}
                placeholder={lang.LOCATIONS_COUNTRY_SELECT}
                disabled={isReadMode}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label={lang.EVENT_ZIP_CODE}
                disabled={isReadMode}
                {...form.getInputProps('postalCode')}
                value={form.values.postalCode}
              />
            </Grid.Col>
          </Grid>
          <Grid grow gap="lg" className="coordinates">
            <Grid.Col span={{ xs: 12, sm: 9, md: 8, lg: 8 }}>
              <TextInput
                label={`${lang.GLOBAL_LATITUDE} & ${lang.GLOBAL_LONGITUDE}`}
                placeholder="ex: 38.81197384388527, -9.21435085836787"
                value={coordinatesInput}
                disabled={isReadMode}
                onChange={e => setCoordinatesInput(e.target.value)}
                onBlur={() => {
                  const [latStr, lngStr] = coordinatesInput.split(',');
                  const lat = parseFloat(latStr?.trim());
                  const lng = parseFloat(lngStr?.trim());
                  if (!isNaN(lat)) form.setFieldValue('lat', lat);
                  if (!isNaN(lng)) form.setFieldValue('lng', lng);
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ xs: 12, sm: 3, md: 4, lg: 4 }}>
              <Button
                variant={!form.values.country ? 'outline' : 'filled'}
                onClick={() => setIsMapOpen(prev => !prev)}
                disabled={isReadMode || !form.values.country}
                className="map-button"
                type="button"
                radius="md"
              >
                {isMapOpen ? lang.LOCATIONS_CLOSE_MAP : lang.LOCATIONS_OPEN_MAP}
              </Button>
            </Grid.Col>

            {isMapOpen && (
              <Grid.Col span={12}>
                <MapCoordinates
                  address={form.values.address}
                  postalCode={form.values.postalCode}
                  country={form.values.country || ''}
                  lat={form.values.lat}
                  lng={form.values.lng}
                  onSelectCoordinates={(lat, lng) => {
                    form.setFieldValue('lat', lat);
                    form.setFieldValue('lng', lng);
                  }}
                />
              </Grid.Col>
            )}
          </Grid>
          <Accordion variant="unstyled" radius="md" className="accordion">
            <Accordion.Item value="additional-info">
              <Accordion.Control>{lang.GLOBAL_ADDITIONAL_INFORMATION}</Accordion.Control>
              <Accordion.Panel>
                <Group className="locals-currency-table">
                  <Header pageTitle={lang.GLOBAL_CURRENCY} />
                  <CurrencyTable
                    selectedCurrencies={selectedCurrencies}
                    currencyList={currencyList}
                    loading={loading}
                    editable={!isReadMode}
                    filteredCid={filteredCid}
                    onAddCurrency={handleAddCurrency}
                    onRemoveCurrency={handleRemoveCurrency}
                    form={form}
                    entity={{ entid: location?.entid || 0 }}
                    declarationAmount={declarationAmount}
                    isNewLocation={mode === MODE.NEW}
                    onCurrencyChange={(cid, field, centsValue) => {
                      setSelectedCurrencies(prev => {
                        const updated = prev.map(c => (c.cid === cid ? { ...c, [field]: centsValue } : c));

                        form.setFieldValue('currencies', updated);
                        return updated;
                      });
                    }}
                    maxCurrencies={maxCurrencies}
                  />
                </Group>

                <JsonInput
                  label={lang.ENTITY_EXTERNAL_DATA}
                  placeholder={`${lang.ENTITY_EXTERNAL_DATA} (JSON)`}
                  value={form.values.external}
                  onChange={value => form.setFieldValue('external', value)}
                  minRows={1}
                  disabled={isReadMode}
                />

                <TextInput
                  label={'C.B. Endpoint'}
                  placeholder={'Core Bank Endpoint'}
                  {...form.getInputProps('cbendpoint')}
                  value={form.values.cbendpoint}
                  disabled={isReadMode}
                />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {!isReadMode && (
            <Group>
              {(isDefaultMode || mode === MODE.NEW || mode === undefined) && (
                <Button radius="md" color="#e4ae00" onClick={handleClearForm} type="button">
                  {lang.GLOBAL_NEW}
                </Button>
              )}

              <Button
                radius="md"
                type="submit"
                color="#538539"
                onClick={() => {
                  setNewLocation(true);
                  setTimeout(() => {
                    reloadLocations && reloadLocations();
                  }, 1500);
                }}
              >
                {lang.GLOBAL_SAVE_BUTTON}
              </Button>

              {(isDefaultMode || mode === MODE.EDIT) && (
                <Button
                  radius="md"
                  color="#861724"
                  onClick={() => {
                    handleDeleteLocation(location?.entid || 0, form.values.sid || 0);
                  }}
                  type="button"
                >
                  {lang.GLOBAL_DELETE}
                </Button>
              )}
            </Group>
          )}
        </form>
      ) : (
        <Text fs="italic">{lang.LOCATION_CHOOSE_ENTID}</Text>
      )}
    </Group>
  );
};

export default LocationsForm;
